import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

type ConsentStatus = "pending" | "all" | "necessary";

const STORAGE_KEY = "lotus-flow-consent";

interface CookieConsentContextValue {
  consent: ConsentStatus;
  isPending: boolean;
  hasAnalytics: boolean;
  acceptAll: () => void;
  acceptNecessary: () => void;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function readStoredConsent(): ConsentStatus | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "all" || raw === "necessary") return raw;
    return null;
  } catch {
    return null;
  }
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentStatus>("pending");

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) setConsent(stored);
  }, []);

  const acceptAll = useCallback(() => {
    setConsent("all");
    try {
      window.localStorage.setItem(STORAGE_KEY, "all");
    } catch {
      /* ignore */
    }
  }, []);

  const acceptNecessary = useCallback(() => {
    setConsent("necessary");
    try {
      window.localStorage.setItem(STORAGE_KEY, "necessary");
    } catch {
      /* ignore */
    }
  }, []);

  const resetConsent = useCallback(() => {
    setConsent("pending");
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        isPending: consent === "pending",
        hasAnalytics: consent === "all",
        acceptAll,
        acceptNecessary,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return ctx;
}
